import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigateWithParams } from '@/shared/hooks/use-navigate-with-params';
import { AppRoutes } from '@/shared/lib/routes';
import { ANALYTICS_EVENTS, trackEvent } from '@/features/analytics';
import { signOut } from '@/features/auth';
import { useSignIn } from '@/features/auth/hooks/use-sign-in';
import { usePurchaseStore } from '@/stores/purchase-store';
import { useQuizStore } from '@/stores/quiz-store';
import { useClaimsStore } from '../store';
import { completePostAuth, PostAuthError, reopenSigningStep } from '../domain/post-auth';

export interface UsePostAuthCompletionOptions {
  /** When true, the pipeline only runs after a successful guest purchase. */
  requirePurchase: boolean;
  /** Called after finalize + filing succeed. */
  onComplete?: () => void;
}

function usePersistedStoresHydrated(): boolean {
  const [hydrated, setHydrated] = useState(
    () =>
      usePurchaseStore.persist.hasHydrated() &&
      useClaimsStore.persist.hasHydrated() &&
      useQuizStore.persist.hasHydrated(),
  );

  useEffect(() => {
    const check = () => {
      if (
        usePurchaseStore.persist.hasHydrated() &&
        useClaimsStore.persist.hasHydrated() &&
        useQuizStore.persist.hasHydrated()
      ) {
        setHydrated(true);
      }
    };
    const unsubPurchase = usePurchaseStore.persist.onFinishHydration(check);
    const unsubClaims = useClaimsStore.persist.onFinishHydration(check);
    const unsubQuiz = useQuizStore.persist.onFinishHydration(check);
    check();
    return () => {
      unsubPurchase();
      unsubClaims();
      unsubQuiz();
    };
  }, []);

  return hydrated;
}

export function usePostAuthCompletion({ requirePurchase, onComplete }: UsePostAuthCompletionOptions) {
  const navigate = useNavigateWithParams();
  const hydrated = usePersistedStoresHydrated();
  const { status: authStatus, user, handleApple, handleGoogle } = useSignIn();
  const purchaseStatus = usePurchaseStore((s) => s.status);
  const purchaseSubId = usePurchaseStore((s) => s.paymentIntentId);
  const purchasePlanId = usePurchaseStore((s) => s.planId);
  const email = (useQuizStore((s) => s.answers.email) as string) || '';

  const [running, setRunning] = useState(false);
  const [error, setError] = useState<PostAuthError | null>(null);
  const ranForUid = useRef<string | null>(null);

  const run = useCallback(async () => {
    if (!user) return;
    setRunning(true);
    setError(null);
    try {
      await completePostAuth({
        uid: user.uid,
        email: email || user.email || '',
        appleToken: user.appleToken,
        purchaseStatus,
        purchaseSubId,
        purchasePlanId,
      });
      trackEvent(ANALYTICS_EVENTS.SIGN_UP, { userId: user.uid });
      onComplete?.();
    } catch (err) {
      const next =
        err instanceof PostAuthError
          ? err
          : new PostAuthError('Something went wrong. Please retry.', 'retry');
      console.error('post-auth completion failed', err);
      setError(next);
      setRunning(false);
    }
  }, [user, email, purchaseStatus, purchaseSubId, purchasePlanId, onComplete]);

  useEffect(() => {
    if (!hydrated) return;
    if (authStatus !== 'authenticated' || !user) {
      ranForUid.current = null;
      return;
    }
    if (requirePurchase && purchaseStatus !== 'success') return;
    if (ranForUid.current === user.uid) return;
    ranForUid.current = user.uid;
    void run();
  }, [hydrated, authStatus, user, requirePurchase, purchaseStatus, run]);

  const retry = useCallback(() => {
    void run();
  }, [run]);

  const resign = useCallback(() => {
    reopenSigningStep();
    navigate({ to: AppRoutes.QUIZ });
  }, [navigate]);

  const relogin = useCallback(async () => {
    setError(null);
    ranForUid.current = null;
    await signOut();
  }, []);

  return {
    authStatus,
    user,
    handleApple,
    handleGoogle,
    running,
    error,
    purchaseStatus,
    hydrated,
    retry,
    resign,
    relogin,
  };
}
